<template>
  <section :class="[$options.name, { mobile: isMobile }, { disabled: busy }]">
    <p v-if="loginError" class="tw:mb-2" style="font-size: 0.875rem; color: red">
      {{ loginError }}
    </p>
    <EdroneInput
      id="email-input"
      v-model.trim="v$.userEmail.$model"
      :error-msg="
        v$.userEmail.$dirty && v$.userEmail.$error
          ? translations.inputs.email.errors[v$.userEmail.$errors[0].$message]
          : null
      "
      :invalid="v$.userEmail.$dirty && (v$.userEmail.email.$invalid || v$.userEmail.required.$invalid)"
      :label="translations.inputs.email.label"
      :placeholder="translations.inputs.email.placeholder"
      class="required-inputs tw:mb-4"
      type="email"
      unique-id="b07130e5-2251-4b5c-9cac-4c020163f842"
      @enter="handleNextInput"
      @input="handleEmailInput"
    />
    <EdroneInput
      v-model.trim="v$.userPassword.$model"
      :error-msg="
        v$.userPassword.$dirty && v$.userPassword.$error
          ? translations.inputs.password.errors[v$.userPassword.$errors[0].$message]
          : null
      "
      :invalid="v$.userPassword.$dirty && v$.userPassword.required.$invalid"
      :label="translations.inputs.password.label"
      :placeholder="translations.inputs.password.placeholder"
      class="required-inputs tw:mb-2"
      type="password"
      unique-id="d8c3547a-b608-4734-ac0e-a3dc7d16105a"
      @enter="handleNextInput"
      @input="handlePasswordInput"
    />

    <a
      href="/forgot_password"
      style="margin: 0 0 1rem; font-size: 0.875rem; color: #0d9ce1; display: block; text-decoration: none; float: right"
    >
      {{ translations.buttons.password }}
    </a>

    <div
      id="g-recaptcha"
      ref="refRecaptchaDiv"
      class="tw:!my-8"
      style="width: fit-content; height: fit-content; margin: 0 auto"
    />
    <EdroneButton
      :disabled="!testRecaptcha || !userEmail || !userPassword"
      class="required-inputs tw:mb-4"
      style="width: 100%"
      type="solid"
      unique-id="c4291173-4ca9-41dc-bbd3-205a7362420a"
      @click="printResult"
    >
      {{ translations.buttons.login }}
    </EdroneButton>

    <p v-if="!isEnabledWebView" style="font-size: 0.875rem; text-align: center">
      {{ translations.register.text }}
      <a :href="'/register' + query" style="color: #0d9ce1; text-decoration: none" @click="$emit('register')">
        {{ translations.register.action }}
      </a>
    </p>
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import axios from "vue-app/utils/axios";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

export default defineComponent({
  name: "AccountLoginForm",
  components: {
    EdroneInput,
    EdroneButton,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["register"],
  setup() {
    const query = window.location.search;
    const translations = window._trans._account.login;
    const refRecaptchaDiv = ref(null);

    const userEmail = ref("");
    const userPassword = ref("");
    const loginError = ref("");

    const rules = {
      userEmail: { required, email },
      userPassword: { required },
    };
    const v$ = useVuelidate(rules, { userEmail, userPassword });

    function handleEmailInput() {
      // console.log(userEmail.value)
    }

    function handlePasswordInput() {
      // console.log(userPassword.value)
    }

    onMounted(() => {
      renderRecaptcha();
    });

    function handleNextInput(event: KeyboardEvent) {
      const inputs = document.querySelectorAll(".required-inputs input, .required-inputs.edrone-button");
      const index = Array.from(inputs).indexOf(event.target as HTMLElement);
      if (index === 0) {
        v$.value.userEmail.$touch();
      }
      if (index === 1) {
        v$.value.userPassword.$touch();
      }
      (inputs[index + 1] as HTMLElement)?.focus();
      if (index === 1 && !!userPassword.value) {
        printResult();
      }
    }

    const busy = ref<boolean>(false);

    function printResult() {
      v$.value.userEmail.$touch();
      v$.value.userPassword.$touch();

      if (!v$.value.userEmail.$error && !v$.value.userPassword.$error) {
        loginUser();
      }
    }

    async function loginUser() {
      try {
        amplitudeSendEvent("login-button-click");
        if (!testRecaptcha.value) {
          // @ts-ignore
          // eslint-disable-next-line no-undef
          window.grecaptcha?.reset();
          testRecaptcha.value = "";
          loginError.value = "Recaptcha verification is required.";
          return;
        }

        busy.value = true;
        loginError.value = "";
        let bodyFormData = new FormData();
        bodyFormData.append("email", userEmail.value);
        bodyFormData.append("password", userPassword.value);
        bodyFormData.append("g-recaptcha-response", testRecaptcha.value);
        bodyFormData.append("_remember_me", "true");

        const response = await axios({
          method: "post",
          url: "/login",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          amplitudeSendEvent("login-success");
          window.location.href = "/login/trackers";
        } else {
          amplitudeSendEvent("login-error");
          // @ts-ignore
          // eslint-disable-next-line no-undef
          window.grecaptcha?.reset();
          testRecaptcha.value = "";
          if (response.data.status === "error") {
            if (response.data.message === "Your email address is not assigned to any workspace") {
              loginError.value = translations.errors.unlinked;
            } else {
              loginError.value = translations.errors.invalid;
            }
          } else {
            loginError.value = translations.errors.expired;
          }
          busy.value = false;
        }
      } catch (error) {
        // @ts-ignore
        // eslint-disable-next-line no-undef
        window.grecaptcha?.reset();
        testRecaptcha.value = "";
        loginError.value = translations.errors.other;
        busy.value = false;
      }
    }

    const testRecaptcha = ref(!window.isCaptchaRequired); // isCaptchaRequired is `undefined` when not required, then `testRecaptcha` is set to true so the login button can be enabled

    const assignRecaptcha = () => {
      // @ts-ignore
      // eslint-disable-next-line no-undef
      testRecaptcha.value = window.grecaptcha?.getResponse();
    };

    // @ts-ignore
    window.assignRecaptcha = assignRecaptcha; // is needed for recaptcha to work without error in console

    function renderRecaptcha() {
      const interval = setInterval(() => {
        if (
          // @ts-ignore
          // eslint-disable-next-line no-undef
          typeof window.grecaptcha === "object" &&
          // @ts-ignore
          // eslint-disable-next-line no-undef
          typeof window.grecaptcha?.render === "function" &&
          refRecaptchaDiv.value
        ) {
          clearInterval(interval);
          // @ts-ignore
          // eslint-disable-next-line no-undef
          window.grecaptcha?.render(refRecaptchaDiv.value, {
            callback: "assignRecaptcha",
            "error-callback": "assignRecaptcha",
            "expired-callback": "assignRecaptcha",
            sitekey: import.meta.env.VITE_RECAPTCHA_SITEKEY,
          });
        }
      }, 60);
    }

    const isEnabledWebView = ReactNativeBridge.checkIsWebViewMode();

    return {
      query,
      printResult,
      refRecaptchaDiv,
      busy,
      translations,
      userEmail,
      handleEmailInput,
      userPassword,
      handlePasswordInput,
      v$,
      handleNextInput,
      loginError,
      testRecaptcha,
      assignRecaptcha,
      isEnabledWebView,
    };
  },
});
</script>
